import appInfo from "@common/appInfo";
import { type Validation } from "@common/controls/Form/component";
import { isVimeoURL } from "@common/controls/Video/Vimeo/lib";
import { isVzaarURL } from "@common/controls/Video/Vzaar/lib";
import { isWistiaURL } from "@common/controls/Video/Wistia/lib";
import { isYouTubeURL } from "@common/controls/Video/YouTube/lib";
import { System } from "@common/declarations";
import { type I18n } from "@common/lib/i18n";
import { isFunction } from "lodash";
import apiV1 from "./api";
import { parseSouthAfricaId } from "./id";

export const validateValue = (
  value: any,
  validation: System.FormFieldValidationSettings,
  formControlKey: System.FormControlKey
): string | null => {
  const { pattern, message, formFieldValidationTypeKey } = validation;

  switch (formFieldValidationTypeKey) {
    case "Regex":
      return !value.match(new RegExp(pattern)) ? message : null;
    case "MaxLength":
      return value.length > +pattern ? message : null;
    case "MinLength":
      return value.length < +pattern ? message : null;
    case "GreaterThan":
      switch (formControlKey) {
        case "Date":
        case "DateTime":
          return new Date(value) <= new Date(pattern) ? message : null;
        case "DateRange":
          return new Date(value.from) <= new Date(pattern) ? message : null;
        default:
          return value <= +pattern ? message : null;
      }
    case "LessThan":
      switch (formControlKey) {
        case "Date":
        case "DateTime":
          return new Date(value) >= new Date(pattern) ? message : null;
        case "DateRange":
          return new Date(value.from) >= new Date(pattern) ? message : null;
        default:
          return value >= +pattern ? message : null;
      }
    case "Gender":
      switch (formControlKey) {
        case "SouthAfricanIdNumber":
          const southAfricaId = parseSouthAfricaId(value);
          return southAfricaId?.gender !== pattern ? message : null;
        default:
          return null;
      }
    case "YoungerThan":
      switch (formControlKey) {
        case "SouthAfricanIdNumber":
          const southAfricaId = parseSouthAfricaId(value);
          return !southAfricaId || southAfricaId.age >= +pattern ? message : null;
        default:
          return null;
      }
    case "OlderThan":
      switch (formControlKey) {
        case "SouthAfricanIdNumber":
          const southAfricaId = parseSouthAfricaId(value);
          return !southAfricaId || southAfricaId.age <= +pattern ? message : null;
        default:
          return null;
      }
    default:
      return null;
  }
};

export enum ValidationErrors {
  MaxLength = "Exceeds maximum allowed length",
  Required = "Required",
  RegexFormat = "Wrong format"
}

type ValidateValueArgs = {
  isRequired: boolean;
  isTrueRequired: boolean;
  options?: Validation;
};
const isNoValue = (value: any): boolean => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && !value.length) return true;
  if (typeof value === "object") return false;
  if (typeof value === "string") return !$(`<div>${value.trim()}</div>`).text().trim();

  return !value.toString().trim();
};
export const validateValueEx = async (
  i18n: I18n,
  value: any,
  args: ValidateValueArgs
): Promise<string | null | undefined> => {
  if (args.isRequired) {
    if (isNoValue(value) || (args.isTrueRequired && value === false))
      return i18n?.errorRequiredField || ValidationErrors.Required;
  } else if (!value && value !== 0) {
    return null;
  }

  const options = args.options;
  if (options) {
    if (isFunction(options)) {
      return options(i18n, value);
    }

    if (options.primary) {
      const error = await options.primary(i18n, value?.toString());
      if (error) return error;
    }

    return (options.validations ?? []).reduce((res, x) => res ?? validateValue(value, x, options.formControlKey), null);
  }

  return null;
};

const ABSOLUTE_URL_REGEX =
  /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
const RELATIVE_URL_REGEX = /^\/([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
const absoluteUrlValidator = (i18n: I18n, value: string) =>
  value.match(ABSOLUTE_URL_REGEX) ? null : i18n.absoluteUrlValidatorError;
const relativeUrlValidator = (i18n: I18n, value: string) =>
  value.match(RELATIVE_URL_REGEX) ? null : i18n.relativeUrlValidatorError;

/** Absolute or relative url */
const urlValidator = (i18n: I18n, value: string): string | null =>
  !value || !absoluteUrlValidator(i18n, value) || !relativeUrlValidator(i18n, value) ? null : i18n.urlValidatorError;

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
export const emailValidator = (i18n: I18n, value: string): string | null =>
  value.match(EMAIL_REGEX) ? null : i18n.errorInvalidEmail;

export const userEmailValidator = async (i18n: I18n, isHost: boolean, email: string, userId?: number) => {
  if (email.match(EMAIL_REGEX)) {
    const url = isHost ? `${appInfo.integration.hostUrl}/api/v1/validate/email` : `/validate/email`;
    const { error, isTaken } = await apiV1.get<{ error; isTaken }>(i18n, url, {
      email,
      userId
    });
    return error ?? (isTaken ? i18n.fieldEmailInUse : null);
  }

  return i18n.errorInvalidEmail;
};

export const validators = {
  absoluteUrl: absoluteUrlValidator,
  email: emailValidator,
  mailTo: (i18n: I18n, value: string) => {
    if (!value) return null;
    const m = value.match(/mailto\:\s*(.*)/i);
    if (!m) return i18n.urlValidatorError;
    if (emailValidator(i18n, m[1])) return i18n.urlValidatorError;
    return null;
  },
  relativeUrl: relativeUrlValidator,
  url: urlValidator
};

export const maxLengthValidator = (i18n: I18n, maxLength: number, message?: string, value?: string | null) => {
  if (!value) return null;

  return value.length > maxLength ? message ?? i18n.maxLengthError : null;
};

export const getMaxLengthValidator =
  (maxLength: number, message?: string) => (i18n: I18n, value: string | null | undefined) =>
    maxLengthValidator(i18n, maxLength, message, value);

const TRON_VALIDATE_URL = "https://api.shasta.trongrid.io/wallet/validateaddress";

type TronValidatorResult = {
  message: string;
  result: boolean;
};

export const tronValidator = async (i18n: I18n, value: string) => {
  const res = await apiV1.post<TronValidatorResult>(i18n, TRON_VALIDATE_URL, {
    address: value
  });
  return res.result ? null : i18n.invalidWalletAddress;
};

type EthereumValidatorResult = {
  message: string;
  isValid: boolean;
};
export const ethereumValidator = async (i18n: I18n, value: string) => {
  const res = await apiV1.get<EthereumValidatorResult>(i18n, "validate/crypto/ethereum", {
    address: value
  });
  return res.isValid ? null : i18n.invalidWalletAddress;
};

export const getCryptoCurrencyValidator = (currencyCode: string) => {
  switch (currencyCode) {
    case "ETH":
      return ethereumValidator;
    case "TRX":
      return tronValidator;
  }
};

export type VideoUrlProvider = "YouTube" | "Vimeo" | "Vzaar" | "Wistia";

export const videoUrlValidator = (i18n: I18n, url?: string | null, availableProviders?: VideoUrlProvider[]) => {
  if (!url) return null;

  var error = absoluteUrlValidator(i18n, url);
  if (!!error) return error;

  if (!availableProviders || availableProviders.length === 0) return null;

  const isYouTube = isYouTubeURL(url);
  const isWistia = isWistiaURL(url);
  const isVimeo = isVimeoURL(url);
  const isVzaar = isVzaarURL(url);

  if (
    (!isYouTube && !isWistia && !isVimeo && !isVzaar) ||
    (isYouTube && !availableProviders.some((x) => x === "YouTube")) ||
    (isWistia && !availableProviders.some((x) => x === "Wistia")) ||
    (isVimeo && !availableProviders.some((x) => x === "Vimeo")) ||
    (isVzaar && !availableProviders.some((x) => x === "Vzaar"))
  )
    return i18n.unsupportedVideoLink;

  return null;
};
